/**
 * CLI handler for `nexus info` command.
 *
 * Offline diagnostic — no server required.
 */

import { readFileSync } from 'node:fs'
import { dirname, join } from 'node:path'
import { fileURLToPath } from 'node:url'
import { resolveConfig } from '@kernel/config/env.js'
import { getDatabaseType, getDatabasePath } from '@kernel/config/database.js'
import { getProjectPath, getProjectMigrationsDir, findEnvFile } from '@kernel/config/paths.js'
import { discoverPlugins } from '@kernel/config/load-config.js'
import { getAllPluginStates, shortPluginName } from '../core/plugin-ops.js'
import { getOrCreateInstanceInfo } from '@kernel/config/instance-id.js'
import { initCli } from './shared.js'

export interface NexusInfo {
  version: string
  node: string
  platform: string
  database: { type: string; path: string }
  envFile: string | null
  environment: string
  timezone: string
  projectPath: string
  instance: { id: string; machineId: string } | null
  plugins: Array<{ name: string; version: string; enabled: boolean }>
  paths: {
    migrations: string
    data: string
  }
}

/** Read nexus-backend version from package.json */
function getVersion(): string {
  let dir = dirname(fileURLToPath(import.meta.url))
  // Walk up until we find package.json (works from both src/cli/ and dist/)
  for (let i = 0; i < 5; i++) {
    const candidate = join(dir, 'package.json')
    try {
      const pkg = JSON.parse(readFileSync(candidate, 'utf-8'))
      if (pkg.name === '@gzl10/nexus-backend') return pkg.version
    } catch { /* not found, keep going */ }
    dir = dirname(dir)
  }
  return 'unknown'
}

/**
 * Collect system info (pure data, no side effects besides logger init).
 * Separated from display for testability.
 */
export async function collectInfo(): Promise<NexusInfo> {
  const config = resolveConfig()
  const projectPath = getProjectPath()
  const discovered = await discoverPlugins(projectPath)
  const states = getAllPluginStates(projectPath)

  const plugins = discovered.map(p => ({
    name: p.name,
    version: p.version,
    enabled: states[p.name]?.enabled ?? true
  }))

  return {
    version: getVersion(),
    node: process.version,
    platform: `${process.platform} ${process.arch}`,
    database: {
      type: getDatabaseType(),
      path: getDatabasePath()
    },
    envFile: findEnvFile(),
    environment: config.nodeEnv,
    timezone: config.timezone,
    projectPath,
    instance: await (async () => {
      try {
        const info = await getOrCreateInstanceInfo(join(projectPath, 'data'))
        return { id: info.instanceId, machineId: info.machineId }
      } catch {
        return null
      }
    })(),
    plugins,
    paths: {
      migrations: getProjectMigrationsDir(),
      data: join(projectPath, 'data')
    }
  }
}

/** Format and print info to console */
export async function handleInfo(options: { json?: boolean } = {}): Promise<void> {
  initCli()
  const info = await collectInfo()

  if (options.json) {
    console.log(JSON.stringify(info, null, 2))
    return
  }

  console.log('')
  console.log(`  Nexus Backend v${info.version}`)
  console.log(`  Node.js ${info.node} | ${info.platform}`)
  console.log('')
  console.log(`  Database:    ${info.database.type} (${info.database.path})`)
  console.log(`  Env file:    ${info.envFile ?? 'none'}`)
  console.log(`  Environment: ${info.environment}`)
  console.log(`  Timezone:    ${info.timezone}`)
  console.log(`  Project:     ${info.projectPath}`)

  if (info.instance) {
    console.log('')
    console.log('  Instance:')
    console.log(`    ID:      ${info.instance.id}`)
    console.log(`    Machine: ${info.instance.machineId}`)
  }

  if (info.plugins.length > 0) {
    console.log('')
    console.log(`  Plugins (${info.plugins.length}):`)
    for (const p of info.plugins) {
      const icon = p.enabled ? '\u2713' : '\u2717'
      const status = p.enabled ? 'enabled' : 'disabled'
      console.log(`    ${icon} ${shortPluginName(p.name)}  v${p.version}  ${status}`)
    }
  } else {
    console.log('')
    console.log('  Plugins: none')
  }

  console.log('')
  console.log('  Paths:')
  console.log(`    Migrations: ${info.paths.migrations}`)
  console.log(`    Data:       ${info.paths.data}`)
  console.log('')
}
